import { ScannerData } from "../types/ScannerData";

export interface FuturesMapping {
    futuresTicker: string;
    displayName: string;
    etfUnderlying: string;
    category: "index" | "energy" | "metals" | "crypto";
    priceMultiplier: number;
    micro: boolean;
}

export const FUTURES_TO_OPTIONS_MAP: FuturesMapping[] = [
    // --- Index Futures (1:1 with their index, no price conversion) ---
    {
        futuresTicker: "ES",
        displayName: "E-mini S&P 500",
        etfUnderlying: "SPX",
        category: "index",
        priceMultiplier: 1,
        micro: false,
    },
    {
        futuresTicker: "MES",
        displayName: "Micro E-mini S&P 500",
        etfUnderlying: "SPX",
        category: "index",
        priceMultiplier: 1,
        micro: true,
    },
    {
        futuresTicker: "NQ",
        displayName: "E-mini Nasdaq-100",
        etfUnderlying: "QQQ",
        category: "index",
        priceMultiplier: 1,
        micro: false,
    },
    {
        futuresTicker: "MNQ",
        displayName: "Micro E-mini Nasdaq",
        etfUnderlying: "QQQ",
        category: "index",
        priceMultiplier: 1,
        micro: true,
    },
    {
        futuresTicker: "RTY",
        displayName: "E-mini Russell 2000",
        etfUnderlying: "RUT",
        category: "index",
        priceMultiplier: 1,
        micro: false,
    },
    {
        futuresTicker: "M2K",
        displayName: "Micro E-mini Russell",
        etfUnderlying: "RUT",
        category: "index",
        priceMultiplier: 1,
        micro: true,
    },
    {
        futuresTicker: "YM",
        displayName: "E-mini Dow Jones",
        etfUnderlying: "DIA",
        category: "index",
        priceMultiplier: 1,
        micro: false,
    },
    {
        futuresTicker: "MYM",
        displayName: "Micro E-mini Dow",
        etfUnderlying: "DIA",
        category: "index",
        priceMultiplier: 1,
        micro: true,
    },

    // --- Metals Futures (contract-spec ratios: GC=100oz, GLD~0.0937oz/share -> ~1067 shares per contract) ---
    {
        futuresTicker: "GC",
        displayName: "Gold Futures",
        etfUnderlying: "GLD",
        category: "metals",
        priceMultiplier: 11.08,
        micro: false,
    },
    {
        futuresTicker: "MGC",
        displayName: "Micro Gold",
        etfUnderlying: "GLD",
        category: "metals",
        priceMultiplier: 1.108,
        micro: true,
    },
    {
        futuresTicker: "SI",
        displayName: "Silver Futures",
        etfUnderlying: "SLV",
        category: "metals",
        priceMultiplier: 56.5,
        micro: false,
    },
    {
        futuresTicker: "HG",
        displayName: "Copper Futures",
        etfUnderlying: "CPER",
        category: "metals",
        priceMultiplier: 1,
        micro: false,
    },

    // --- Energy Futures ---
    {
        futuresTicker: "CL",
        displayName: "Crude Oil WTI",
        etfUnderlying: "USO",
        category: "energy",
        priceMultiplier: 1,
        micro: false,
    },
    {
        futuresTicker: "MCL",
        displayName: "Micro Crude Oil",
        etfUnderlying: "USO",
        category: "energy",
        priceMultiplier: 1,
        micro: true,
    },
    {
        futuresTicker: "NG",
        displayName: "Natural Gas",
        etfUnderlying: "UNG",
        category: "energy",
        priceMultiplier: 1,
        micro: false,
    },

    // --- Crypto Futures ---
    {
        futuresTicker: "BTC",
        displayName: "Bitcoin Futures",
        etfUnderlying: "IBIT",
        category: "crypto",
        priceMultiplier: 1,
        micro: false,
    },
    {
        futuresTicker: "MBT",
        displayName: "Micro Bitcoin",
        etfUnderlying: "IBIT",
        category: "crypto",
        priceMultiplier: 1,
        micro: true,
    },
];

const futuresLookup = new Map<string, FuturesMapping>();
const etfToFuturesLookup = new Map<string, FuturesMapping[]>();

function buildLookups() {
    futuresLookup.clear();
    etfToFuturesLookup.clear();
    for (const m of FUTURES_TO_OPTIONS_MAP) {
        futuresLookup.set(m.futuresTicker.toUpperCase(), m);
        const key = m.etfUnderlying.toUpperCase();
        const arr = etfToFuturesLookup.get(key) || [];
        arr.push(m);
        etfToFuturesLookup.set(key, arr);
    }
}
buildLookups();

/**
 * Option roots that tape/chain use (weeklies, product codes) map to the same `etfUnderlying`
 * as in FUTURES_TO_OPTIONS_MAP so futures-mode symbol filters do not drop valid rows.
 * Example: tape Ticker SPXW must match when the user selected ES/MES (underlying SPX).
 */
const OPTION_ROOT_TO_INDEX_ETF: Record<string, string> = {
    SPX: "SPX",
    SPXW: "SPX",
    SPXQ: "SPX",
    SPXPM: "SPX",
    XSP: "SPX",
    XSPA: "SPX",
    NDX: "NDX",
    NDXP: "NDX",
    RUT: "RUT",
    RUTW: "RUT",
    RUTQ: "RUT",
    DJX: "DIA",
};

// --- Dynamic multiplier state ---
const MULTIPLIER_CACHE_KEY = "mmec_futures_multipliers";
const MULTIPLIER_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours
let _lastMultiplierFetch = 0;
let _multiplierFetchPromise: Promise<boolean> | null = null;

export interface MultiplierData {
    futuresTicker: string;
    futuresPrice: number | null;
    etfTicker: string;
    etfPrice: number | null;
    indexPrice?: number | null;
    multiplier: number;
    fallback?: boolean;
}

function _loadCachedMultipliers(): boolean {
    try {
        const raw = localStorage.getItem(MULTIPLIER_CACHE_KEY);
        if (!raw) return false;
        const cached = JSON.parse(raw);
        if (!cached || !cached.data || !cached.timestamp) return false;
        const age = Date.now() - cached.timestamp;
        if (age > MULTIPLIER_CACHE_TTL) return false;
        _applyMultipliers(cached.data);
        _lastMultiplierFetch = cached.timestamp;
        return true;
    } catch {
        return false;
    }
}

function _applyMultipliers(data: Record<string, MultiplierData>) {
    let updated = false;
    for (const m of FUTURES_TO_OPTIONS_MAP) {
        const entry =
            data[m.futuresTicker.toUpperCase()] || data[m.futuresTicker];
        if (
            entry &&
            typeof entry.multiplier === "number" &&
            entry.multiplier > 0 &&
            !entry.fallback
        ) {
            // Safety: reject multipliers that are wildly off from the current value
            // (e.g. backend using SPY while frontend expects SPX → 10x mismatch)
            const ratio =
                m.priceMultiplier > 0
                    ? entry.multiplier / m.priceMultiplier
                    : entry.multiplier;
            if (ratio > 5 || ratio < 0.2) continue;
            if (m.priceMultiplier !== entry.multiplier) {
                m.priceMultiplier = entry.multiplier;
                updated = true;
            }
        }
    }
    // Micro contracts: derive from their non-micro parent (1/10th)
    for (const m of FUTURES_TO_OPTIONS_MAP) {
        if (!m.micro) continue;
        const parentTicker = m.futuresTicker.replace(/^M/, "");
        const parent = data[parentTicker.toUpperCase()] || data[parentTicker];
        if (
            parent &&
            typeof parent.multiplier === "number" &&
            parent.multiplier > 0 &&
            !parent.fallback
        ) {
            const microMult = parent.multiplier / 10;
            if (m.priceMultiplier !== microMult) {
                m.priceMultiplier = microMult;
                updated = true;
            }
        }
    }
    if (updated) buildLookups();
}

export async function fetchLiveMultipliers(force = false): Promise<boolean> {
    if (
        !force &&
        _lastMultiplierFetch &&
        Date.now() - _lastMultiplierFetch < MULTIPLIER_CACHE_TTL
    ) {
        return true;
    }
    if (_multiplierFetchPromise && !force) return _multiplierFetchPromise;

    _multiplierFetchPromise = (async () => {
        try {
            const { getApiUrl } = await import("../config/api");
            const url = getApiUrl(
                `/api/futures/multipliers${force ? "?force=1" : ""}`,
            );
            const res = await fetch(url);
            if (!res.ok) return false;
            const json = await res.json();
            if (!json.success || !json.multipliers) return false;

            _applyMultipliers(json.multipliers);
            _lastMultiplierFetch = Date.now();
            localStorage.setItem(
                MULTIPLIER_CACHE_KEY,
                JSON.stringify({
                    data: json.multipliers,
                    timestamp: _lastMultiplierFetch,
                }),
            );
            return true;
        } catch (e) {
            console.warn("Failed to fetch live futures multipliers:", e);
            return false;
        } finally {
            _multiplierFetchPromise = null;
        }
    })();
    return _multiplierFetchPromise;
}

export function getMultiplierAge(): {
    ageMs: number;
    ageLabel: string;
    stale: boolean;
} {
    if (!_lastMultiplierFetch)
        return { ageMs: Infinity, ageLabel: "never", stale: true };
    const ageMs = Date.now() - _lastMultiplierFetch;
    const mins = Math.floor(ageMs / 60000);
    const hours = Math.floor(mins / 60);
    let ageLabel: string;
    if (mins < 1) ageLabel = "just now";
    else if (mins < 60) ageLabel = `${mins}m ago`;
    else if (hours < 24) ageLabel = `${hours}h ago`;
    else ageLabel = `${Math.floor(hours / 24)}d ago`;
    return { ageMs, ageLabel, stale: ageMs > MULTIPLIER_CACHE_TTL };
}

export function getCurrentMultipliers(): Record<
    string,
    { ticker: string; multiplier: number; etf: string }
> {
    const result: Record<
        string,
        { ticker: string; multiplier: number; etf: string }
    > = {};
    for (const m of FUTURES_TO_OPTIONS_MAP) {
        if (!m.micro) {
            result[m.futuresTicker] = {
                ticker: m.futuresTicker,
                multiplier: m.priceMultiplier,
                etf: m.etfUnderlying,
            };
        }
    }
    return result;
}

// Load cached multipliers on module init
_loadCachedMultipliers();

// --- Futures Mode state ---
let _futuresModeEnabled = false; // disabled by default — toggle in top bar
let _selectedFutures: string[] = [];

export function isFuturesModeEnabled(): boolean {
    return _futuresModeEnabled;
}

export function setFuturesModeEnabled(enabled: boolean): void {
    if (_futuresModeEnabled === enabled) return;
    _futuresModeEnabled = enabled;
    if (typeof window !== "undefined") {
        window.dispatchEvent(
            new CustomEvent("mmecFuturesModeChanged", { detail: { enabled } }),
        );
    }
}

export function getSelectedFutures(): string[] {
    return [..._selectedFutures];
}

export function setSelectedFutures(futures: string[]): void {
    _selectedFutures = (futures || [])
        .map((f) =>
            String(f ?? "")
                .trim()
                .toUpperCase(),
        )
        .filter(Boolean);
}

export function getFuturesMapping(
    futuresTicker: string,
): FuturesMapping | undefined {
    const k = String(futuresTicker ?? "").trim();
    if (!k) return undefined;
    return futuresLookup.get(k.toUpperCase());
}

export function getOptionsUnderlying(futuresTicker: string): string {
    const k = String(futuresTicker ?? "").trim();
    if (!k) return "";
    const m = futuresLookup.get(k.toUpperCase());
    return m ? m.etfUnderlying : k;
}

export function getSelectedUnderlyings(): string[] {
    if (!_futuresModeEnabled || !_selectedFutures.length) return [];
    const underlyings = new Set<string>();
    for (const ft of _selectedFutures) {
        const m = futuresLookup.get(ft.toUpperCase());
        if (m) underlyings.add(m.etfUnderlying);
    }
    return Array.from(underlyings);
}

export function getFuturesLabelForUnderlying(
    etfUnderlying: string,
): string | null {
    if (!_futuresModeEnabled) return null;
    const key = String(etfUnderlying ?? "").trim();
    if (!key) return null;
    const matches = etfToFuturesLookup.get(key.toUpperCase());
    if (!matches || !matches.length) return null;
    const selected = matches.find((m) =>
        _selectedFutures.includes(m.futuresTicker.toUpperCase()),
    );
    return selected ? selected.futuresTicker : matches[0].futuresTicker;
}

export function convertPriceToFutures(
    price: number,
    etfUnderlying: string,
): number {
    if (!_futuresModeEnabled || !price) return price;
    const eu = String(etfUnderlying ?? "").trim();
    if (!eu) return price;
    const matches = etfToFuturesLookup.get(eu.toUpperCase());
    if (!matches || !matches.length) return price;
    const selected = matches.find((m) =>
        _selectedFutures.includes(m.futuresTicker.toUpperCase()),
    );
    const mapping = selected || matches[0];
    return price * mapping.priceMultiplier;
}

export function convertPriceFromFutures(
    price: number,
    futuresTicker: string,
): number {
    const ft = String(futuresTicker ?? "").trim();
    if (!ft) return price;
    const m = futuresLookup.get(ft.toUpperCase());
    if (!m || m.priceMultiplier === 0) return price;
    return price / m.priceMultiplier;
}

export function getFuturesByCategory(): Record<string, FuturesMapping[]> {
    const result: Record<string, FuturesMapping[]> = {};
    for (const m of FUTURES_TO_OPTIONS_MAP) {
        if (!result[m.category]) result[m.category] = [];
        result[m.category].push(m);
    }
    return result;
}

export function isUnderlyingSelectedInFuturesMode(underlying: string): boolean {
    if (!_futuresModeEnabled) return true;
    if (!_selectedFutures.length) return true;
    const norm = String(underlying ?? "")
        .trim()
        .toUpperCase();
    if (!norm) return true;
    const selected = getSelectedUnderlyings().map((u) =>
        String(u ?? "")
            .trim()
            .toUpperCase(),
    );
    if (selected.some((u) => u === norm)) return true;
    const bucket = OPTION_ROOT_TO_INDEX_ETF[norm];
    if (bucket && selected.includes(bucket)) return true;
    return false;
}

export function applyFuturesConversionToRow(row: any): any {
    if (!_futuresModeEnabled) return row;
    if (row._futuresMapping) return row;

    const rawUnderlying =
        row["Underlying"] ??
        row.underlyingSymbol ??
        row.underlying_symbol ??
        "";
    const underlying = String(rawUnderlying);
    if (!underlying) return row;

    const norm = underlying.toUpperCase();
    const futuresLabel = getFuturesLabelForUnderlying(norm);
    if (!futuresLabel) return row;

    const mapping = futuresLookup.get(futuresLabel.toUpperCase());
    if (!mapping) return row;

    const mult = mapping.priceMultiplier;
    const converted = { ...row };

    const priceFields = [
        "Last",
        "Strike",
        "Underlying Price",
        "Theoretical Value",
        "Mid",
        "Ask",
        "Bid",
        "last",
        "strike",
        "underlying_price",
        "theoretical_value",
        "mid",
        "ask",
        "bid",
        "Strike Price",
        "High",
        "Low",
        "Open",
        "Close",
        "Previous Close",
    ];

    if (mult !== 1) {
        for (const field of priceFields) {
            if (converted[field] !== undefined && converted[field] !== null) {
                const val = parseFloat(String(converted[field]));
                if (!isNaN(val)) converted[field] = val * mult;
            }
        }
    }

    converted["Underlying"] = futuresLabel;
    converted.underlyingSymbol = futuresLabel;
    converted.underlying_symbol = futuresLabel;
    converted._futuresMapping = {
        original: mapping.etfUnderlying,
        displayed: futuresLabel,
        multiplier: mult,
    };

    return converted;
}

export function applyFuturesConversionToArray(data: any[]): any[] {
    if (!_futuresModeEnabled || !data?.length) return data;
    return data.map(applyFuturesConversionToRow);
}

export function filterDataForFuturesMode(data: any[]): any[] {
    if (!_futuresModeEnabled || !_selectedFutures.length || !data?.length)
        return data;
    const selectedUnderlyings = new Set(
        getSelectedUnderlyings()
            .map((u) =>
                String(u ?? "")
                    .trim()
                    .toUpperCase(),
            )
            .filter(Boolean),
    );
    return data.filter((row) => {
        const raw =
            row["Underlying"] ??
            row.underlyingSymbol ??
            row.underlying_symbol ??
            "";
        const underlying = String(raw).toUpperCase();
        return selectedUnderlyings.has(underlying);
    });
}

// --- Backward-compatible exports (used by existing callers) ---

let symbolMappingEnabled = false;

export function setSymbolMappingEnabled(enabled: boolean): void {
    symbolMappingEnabled = enabled;
    setFuturesModeEnabled(enabled);
}

export function isSymbolMappingEnabled(): boolean {
    return _futuresModeEnabled;
}

export function getMappedSymbol(originalSymbol: string): string {
    const raw = String(originalSymbol ?? "").trim();
    if (!_futuresModeEnabled || !raw) return raw;
    return getFuturesLabelForUnderlying(raw) || raw;
}

export function getRelatedSymbols(symbol: string): string[] {
    const upper = String(symbol ?? "")
        .trim()
        .toUpperCase();
    if (!upper) return [];
    const related = [upper];
    const fMapping = futuresLookup.get(upper);
    if (fMapping) related.push(fMapping.etfUnderlying);
    const etfMappings = etfToFuturesLookup.get(upper);
    if (etfMappings) etfMappings.forEach((m) => related.push(m.futuresTicker));
    return Array.from(new Set(related));
}

export function hasSymbolMapping(symbol: string): boolean {
    if (!_futuresModeEnabled) return false;
    const upper = String(symbol ?? "")
        .trim()
        .toUpperCase();
    if (!upper) return false;
    return futuresLookup.has(upper) || etfToFuturesLookup.has(upper);
}

export function getConversionRatio(
    sourceSymbol: string,
    targetSymbol: string,
): number {
    if (!_futuresModeEnabled) return 1;
    const source = String(sourceSymbol ?? "")
        .trim()
        .toUpperCase();
    const target = String(targetSymbol ?? "")
        .trim()
        .toUpperCase();
    if (!source || !target) return 1;
    if (source === target) return 1;
    const fm = futuresLookup.get(source);
    if (fm && fm.etfUnderlying.toUpperCase() === target)
        return 1 / fm.priceMultiplier;
    const fm2 = futuresLookup.get(target);
    if (fm2 && fm2.etfUnderlying.toUpperCase() === source)
        return fm2.priceMultiplier;
    return 1;
}

export function convertPrice(
    price: number,
    sourceSymbol: string,
    targetSymbol: string,
): number {
    if (!_futuresModeEnabled || !price) return price;
    return price * getConversionRatio(sourceSymbol, targetSymbol);
}

export function applyUnifiedSymbolMapping(item: any): any {
    return applyFuturesConversionToRow(item);
}

export function applyUnifiedSymbolMappingToArray(data: any[]): any[] {
    return applyFuturesConversionToArray(data);
}

export function getMappingStatus() {
    return {
        totalMappings: FUTURES_TO_OPTIONS_MAP.length,
        enabledMappings: FUTURES_TO_OPTIONS_MAP.length,
        mappings: FUTURES_TO_OPTIONS_MAP,
        cacheSize: futuresLookup.size,
        symbolMappingEnabled: _futuresModeEnabled,
        futuresModeEnabled: _futuresModeEnabled,
        selectedFutures: _selectedFutures,
        lastUpdate: Date.now(),
    };
}

export function refreshSymbolMappingCache(): void {
    buildLookups();
}

export async function fetchMarketQuotes(): Promise<Record<string, number>> {
    return {};
}

export function getCurrentMarketQuotes(): Record<string, number> {
    return {};
}

export function areQuotesFresh(): boolean {
    return true;
}

export function getCurrentMarketPrices(): Record<string, number> {
    return {};
}

export function getCurrentMappingRatios(): Record<
    string,
    { original: string; mapped: string; ratio: number; description: string }
> {
    const ratios: Record<
        string,
        { original: string; mapped: string; ratio: number; description: string }
    > = {};
    for (const m of FUTURES_TO_OPTIONS_MAP) {
        ratios[m.futuresTicker] = {
            original: m.futuresTicker,
            mapped: m.etfUnderlying,
            ratio: m.priceMultiplier,
            description: m.displayName,
        };
    }
    return ratios;
}

export function updateMarketPrices(_newPrices: Record<string, number>): void {
    // no-op: prices are derived from contract specs now
}

export function triggerMarketOpenUpdate(): void {
    // no-op
}

export function addSymbolMapping(_mapping: any): void {
    // no-op: futures map is static
}

export interface UnifiedSymbolMapping {
    original: string;
    mapped: string;
    type: "CFD" | "ETF" | "INDEX" | "FUTURES" | "STOCK";
    priceRatio: number;
    strikeRatio: number;
    description: string;
    enabled: boolean;
}

export const UNIFIED_SYMBOL_MAPPINGS: UnifiedSymbolMapping[] =
    FUTURES_TO_OPTIONS_MAP.map((m) => ({
        original: m.futuresTicker,
        mapped: m.etfUnderlying,
        type: "FUTURES" as const,
        priceRatio: m.priceMultiplier,
        strikeRatio: m.priceMultiplier,
        description: m.displayName,
        enabled: true,
    }));
